import { DateTime } from 'luxon';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { CheckboxField } from '../Fields/CheckboxField';
import { HookedForm } from '../HookedForm';
import { Pagination } from '../Pagination/Pagination';
import { SortableHeader } from '../GenericTable/SortableHeader';
import { TagInterface } from '@app/models/util';
import BinIcon from '@app/assets/icons/bin.svg?react';
import DotsIcon from '@app/assets/icons/menu/dot-vertical.svg?react';
import TagIcon from '@app/assets/icons/tag.svg?react';
import UsersIcon from '@app/assets/icons/grid/users.svg?react';

const tableHeader = [
  { label: 'TAG', field: 'name' },
  { label: 'ID', field: 'id' },
  { label: 'CUSTOMERS', field: 'customers' },
  { label: 'CREATION DATE', field: 'createdAt' },
];

export const TagsTable = ({
  tags,
  total,
  page,
  handleNextPage,
  handlePrevPage,
  handleLimit,
  selectedTags,
  handleTagSelect,
  handleDeleteSelected,
  handleDeleteTag,
  canEdit,
  currentSortBy,
  currentSortOrder,
  onSort,
}: {
  tags: TagInterface[];
  total: number;
  page: number;
  handleNextPage: (value: number) => void;
  handlePrevPage: (value: number) => void;
  handleLimit: (value: number) => void;
  selectedTags: string[];
  handleTagSelect: (value: string | undefined) => void;
  handleDeleteSelected: () => void;
  handleDeleteTag: (id: string) => void;
  canEdit: boolean;
  currentSortBy: string;
  currentSortOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
}) => {
  const navigate = useNavigate();
  const showBulkActions = canEdit && selectedTags.length > 0;

  return (
    <div className="bg-white overflow-x-auto">
      <HookedForm>
        <table className="w-full">
          <thead>
            <tr className="border-b border-base-antiFlash ">
              <th className="pl-4 pr-0 py-4 min-w-fit w-0">
                {canEdit && <CheckboxField onCheckMark={handleTagSelect} name="tags" value="all" checked={tags.length === selectedTags.length} />}
              </th>
              {showBulkActions ? (
                <th colSpan={4} className="text-sm text-brand-baseBlue font-medium p-4" align="left">
                  <button type="button" className="flex items-center gap-1" onClick={handleDeleteSelected}>
                    <BinIcon /> Delete tag(s)
                  </button>
                </th>
              ) : (
                tableHeader.map((item) => (
                  <SortableHeader key={item.field} label={item.label} field={item.field} currentSortBy={currentSortBy} currentSortOrder={currentSortOrder} onSort={onSort} />
                ))
              )}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tags.map((tag) => {
              const isSelected = selectedTags.includes(tag.id);
              const customersCount = tag.customersCount ?? 0;
              const goToTag = () => navigate(`/customers/tags/${tag.id}`);

              return (
                <tr
                  key={tag.id}
                  className={`text-xs cursor-pointer w-full ${
                    isSelected ? 'bg-[#F1F9FF] border-t-2 border-b-2 border-t-[#A8DBFF] border-b-[#A8DBFF]' : 'border-b border-base-antiFlash hover:bg-brand-lightBlue3 last-of-type:border-b-0'
                  }`}
                >
                  <td className="p-4 min-w-fit w-0">{canEdit && <CheckboxField onCheckMark={handleTagSelect} name="id" value={tag.id} checked={isSelected} />}</td>
                  <td className="p-4 flex items-center gap-2" onClick={goToTag}>
                    <div className="bg-base-antiFlash rounded-sm size-6 flex items-center justify-center">
                      <TagIcon />
                    </div>
                    {tag.name}
                  </td>
                  <td className="p-4 text-base-davyGray" onClick={goToTag}>
                    #{tag.id}
                  </td>
                  <td className="p-4" onClick={goToTag}>
                    {customersCount === 0 ? (
                      <span>No customers</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="p-1 inline-flex bg-base-antiFlash rounded-md">
                          <UsersIcon width={15} height={15} />
                        </span>
                        {tag.customers?.map((customer) => `${customer.firstName} ${customer.lastName}`).join(', ')}
                        {customersCount > 3 && (
                          <span className="w-fit px-1 h-5 items-center justify-center inline-flex bg-brand-lightBlue1 rounded-md text-xs font-bold text-brand-darkBlue3">+ {customersCount - 3}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-4 text-base-davyGray" onClick={goToTag}>
                    {DateTime.fromISO(tag.createdAt).toFormat('LLL dd, yyyy  HH:mm')}
                  </td>
                  <td className="p-4">
                    <Menu>
                      <MenuButton className="group/user flex items-center justify-center gap-2">
                        <DotsIcon />
                      </MenuButton>
                      <MenuItems
                        transition
                        anchor="left start"
                        className="p-2 bg-white shadow-lg mt-2 min-w-[220px] transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 rounded-lg border file:border-base-antiFlash"
                      >
                        <MenuItem>
                          <Link className="flex items-center gap-2 p-2 rounded-lg data-[focus]:bg-brand-lightBlue4 text-xs w-full" to={`/customers/tags/${tag.id}`}>
                            <TagIcon /> Open tag
                          </Link>
                        </MenuItem>
                        {canEdit && (
                          <MenuItem>
                            <button type="button" className="flex items-center gap-2 p-2 rounded-lg data-[focus]:bg-brand-lightBlue4 text-xs w-full" onClick={() => handleDeleteTag(tag.id)}>
                              <BinIcon className="[&_path]:stroke-system-error" /> Delete tag
                            </button>
                          </MenuItem>
                        )}
                      </MenuItems>
                    </Menu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination handleLimitChange={handleLimit} handleNextPage={handleNextPage} handlePrevPage={handlePrevPage} page={page} total={total} />
      </HookedForm>
    </div>
  );
};
